<template>
  <div class="product-selector">
    <!-- 商品卡片 -->
    <div
      v-for="(item, index) in productList"
      :key="item.uid"
      class="product-card"
    >
      <div class="card-header">
        <span>商品 {{ index + 1 }}</span>
        <el-button
          v-if="productList.length > 1"
          type="danger"
          link
          @click="removeProduct(index)"
        >
          删除
        </el-button>
      </div>



      <!-- 商品选择 -->
      <el-form-item label="选择商品" required>
        <el-input
          v-model="item.spuName"
          placeholder="请选择商品"
          readonly
          @click="openDialog(index)"
        />
      </el-form-item>
    </div>

    <!-- 新增商品 (手动添加空行) -->
    <el-button
      type="primary"
      plain
      :disabled="productList.length >= MAX_COUNT"
      @click="addProduct"
    >
      新增商品
    </el-button>
    <span v-if="productList.length >= MAX_COUNT" class="limit-tip">
      最多选择 {{ MAX_COUNT }} 个商品
    </span>

    <!-- 选择商品弹窗 -->
    <el-dialog
      v-model="dialogVisible"
      title="选择商品 (可多选)"
      width="70%"
      :close-on-click-modal="false"
    >
      <!-- 搜索 -->
      <el-form :inline="true" @submit.prevent>
        <el-form-item label="商品名称">
          <el-input
            v-model="query.spuName"
            placeholder="请输入商品名称"
            clearable
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="search">搜索</el-button>
          <el-button @click="reset">重置</el-button>
        </el-form-item>
      </el-form>

      <!-- 表格 (修改为多选) -->
      <el-table
        ref="tableRef"
        :data="spuList"
        highlight-current-row
        row-key="spuCode"
        @selection-change="handleSelectionChange"
      >
        <!-- 多选框 -->
        <el-table-column
          type="selection"
          width="55"
          :selectable="checkSelectable"
        />
        <el-table-column prop="spuCode" label="SPU Code" width="180" />
        <el-table-column prop="spuName" label="商品名称" />

        <!-- 如果需要单独选择按钮可以保留，或者去掉操作列完全依赖多选 -->
        <!-- <el-table-column label="操作" width="120" align="center">...</el-table-column> -->
      </el-table>

      <span v-if="!hasNextPage" class="no-more">
        没有更多数据了
      </span>

      <!-- 分页 -->
<!--      <div class="pager">-->
<!--        <el-pagination-->
<!--          layout="prev, next"-->
<!--          :current-page="query.pageIndex"-->
<!--          :page-size="query.pageSize"-->
<!--          :disabled="!hasNextPage && query.pageIndex === 1"-->
<!--          @current-change="changePage"-->
<!--        />-->
<!--      </div>-->

      <template #footer>
        <div class="dialog-footer">
          <span class="selected-count">已选 {{ multipleSelection.length }} 项</span>
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="confirmSelect">确定</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { deepClone } from "@zto/zpage-core/utils/lodash"
import { useCurrentAppInstance } from '@zto/zpage'
import { ElMessage } from 'element-plus' // 假设使用了 element-plus

const props = withDefaults(
  defineProps<{
    model: Record<string, any>
    prop: string
    hidden?: boolean
    disabled?: boolean,
    labelProp: string
  }>(),
  {
    hidden: false,
    disabled: false,
  }
)

const app = useCurrentAppInstance()
const MAX_COUNT = 1000
const hasNextPage = ref(true)

/* 商品结构 */
const createItem = () => ({
  uid: Date.now() + Math.random(),
  imageFile: null,
  imageUrl: '',
  spuCode: '',
  spuName: ''
})

/* 数据绑定 */
const innerValue = computed<any[]>({
  get() {
    return props.model[props.labelProp]
  },
  set(val) {
    if (props.labelProp && props.model) {
      props.model[props.labelProp] = val
    }
  }
})

const productList = computed({
  get() {
    return innerValue.value?.length
      ? innerValue.value
      : [createItem()]
  },
  set(val) {
    innerValue.value = val
  }
})

/* 弹窗状态 */
const dialogVisible = ref(false)
const currentIndex = ref(0)
const tableRef = ref()

/* 查询参数 */
const query = ref({
  spuName: '',
  pageIndex: 1,
  pageSize: 10000
})

const spuList = ref([])
const multipleSelection = ref([]) // 存储当前选中的行

/* 已选 spuCode (用于禁用) */
const selectedCodes = computed(() =>
  productList.value.map(i => i.spuCode).filter(Boolean)
)

/* 表格是否可勾选逻辑 */
const checkSelectable = (row) => {
  // 当前正在编辑的那一行所选的 spuCode 不需要禁用 (允许它保持被选中状态或更改)
  // 但为了简化逻辑，通常只要已经在列表中存在(且不是当前行原有值)，就禁用
  const currentItemCode = productList.value[currentIndex.value]?.spuCode
  if (row.spuCode === currentItemCode) return true

  return !selectedCodes.value.includes(row.spuCode)
}

onMounted(() => {
  if (props.model?.spuGoodsList && props.model.spuGoodsList.length) {
    innerValue.value = props.model.spuGoodsList
  }
})

/* 新增 / 删除 */
const addProduct = () => {
  if (productList.value.length >= MAX_COUNT) return
  const productListAdd = deepClone(productList.value)
  productListAdd.push(createItem())
  props.model[props.labelProp] = productListAdd
}

const removeProduct = index => {
  const productListRemove = deepClone(productList.value)
  productListRemove.splice(index, 1)
  props.model[props.labelProp] = productListRemove
}

/* 图片上传 & 回显 */
const onImageChange = (file, index) => {
  const raw = file.raw
  productList.value[index].imageFile = raw
  productList.value[index].imageUrl = URL.createObjectURL(raw)
}

/* 打开弹窗 */
const openDialog = async index => {
  currentIndex.value = index
  dialogVisible.value = true
  multipleSelection.value = [] // 重置选中

  // 如果需要回显当前行已选的，可以在这里处理，
  // 但因为支持多选且翻页，简单的做法是重置，或者仅在当前页高亮

  // 必须重置分页，否则搜索状态会乱
  query.value.pageIndex = 1
  await fetchSpuList()

  // 尝试回显当前行的勾选状态 (可选)
  const currentCode = productList.value[index].spuCode
  if (currentCode && tableRef.value) {
    const row = spuList.value.find(i => i.spuCode === currentCode)
    if (row) {
      tableRef.value.toggleRowSelection(row, true)
    }
  }
}

/* 查询商品 */
const fetchSpuList = async () => {
  const res = await app.request({
    api: 'admin/goods/querySpuInfoList',
    params: query.value
  })

  const list = res?.filter(item=>item.goodsStatus !== 'off_shelf') || []
  spuList.value = list
  hasNextPage.value = list.length === query.value.pageSize
}

/* 多选变化事件 */
const handleSelectionChange = (val) => {
  multipleSelection.value = val
}

/* 确定选择 (核心修改逻辑) */
/* 确定选择 (简化版本 - 每次用新选择完全替换) */
const confirmSelect = () => {
  const selection = multipleSelection.value
  if (!selection.length) {
    ElMessage.warning('请至少选择一个商品')
    return
  }

  // 1. 从当前列表中移除当前行的商品（如果需要替换）
  const currentList = deepClone(productList.value)
  const currentItem = currentList[currentIndex.value]
  const currentCode = currentItem?.spuCode

  // 2. 创建去重映射
  const codeMap = new Map()

  // 2.1 先添加当前列表中除当前行外的商品
  currentList.forEach((item, index) => {
    if (index !== currentIndex.value && item.spuCode) {
      codeMap.set(item.spuCode, item)
    }
  })

  // 2.2 添加新选择的商品（会覆盖重复的）
  selection.forEach(item => {
    codeMap.set(item.spuCode, {
      ...createItem(),
      spuCode: item.spuCode,
      spuName: item.spuName
    })
  })

  // 3. 转换为数组
  const resultList = Array.from(codeMap.values())

  // 4. 检查数量限制
  if (resultList.length > MAX_COUNT) {
    ElMessage.warning(`最多只能选择 ${MAX_COUNT} 个商品`)
    return
  }

  // 5. 更新
  props.model[props.labelProp] = resultList
  dialogVisible.value = false

  // 6. 重置选中状态
  if (tableRef.value) {
    tableRef.value.clearSelection()
  }
  multipleSelection.value = []
}
/* 搜索 / 重置 */
const search = () => {
  query.value.pageIndex = 1
  fetchSpuList()
}

const reset = () => {
  query.value.spuName = ''
  query.value.pageIndex = 1
  fetchSpuList()
}

/* 分页 */
const changePage = page => {
  if (page > query.value.pageIndex && !hasNextPage.value) return
  if (page < 1) return
  query.value.pageIndex = page
  fetchSpuList()
}
</script>

<style scoped>
.product-card {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 16px 16px 4px;
  margin-bottom: 16px;
  background: #fff;
}

.card-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  font-weight: 500;
}

.image-uploader {
  display: inline-block;
}

.image-preview {
  width: 120px;
  height: 92px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #ebeef5;
}

.tip {
  margin-left: 12px;
  font-size: 12px;
  color: #999;
}

.limit-tip {
  margin-left: 12px;
  font-size: 12px;
  color: #f56c6c;
}

.pager {
  margin-top: 16px;
  text-align: right;
}

.no-more {
  margin-left: 12px;
  color: #999;
  font-size: 12px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.selected-count {
  margin-right: 16px;
  color: #666;
  font-size: 14px;
}
</style>
